<script setup lang="ts">
import { TheFooter, TheNavbar, TheSnackbar } from '@/components';
import AppNotificationsContainer from '~/components/AppNotificationsContainer.vue';
import { useNotification } from '~/composables/useNotification';
import { useNotificationsStore } from '~/store/notifications';

const route = useRoute();
const notificationsStore = useNotificationsStore();

// Initialize auto-fetch for notifications
const { initAutoFetch } = useNotification();
initAutoFetch();

const showNavbar = computed((): boolean => {
  if (['showcase'].includes(route.name.split('-')[0])) {
    return false;
  }

  return true;
});

// Keyboard shortcuts and dynamic positioning
onMounted(() => {
  // ESC key to close drawer
  const handleEsc = (event: KeyboardEvent) => {
    if (event.key === 'Escape' && notificationsStore.isDrawerOpen) {
      notificationsStore.closeDrawer();
    }
  };

  document.addEventListener('keydown', handleEsc);

  // Watch for drawer open/close and apply dynamic positioning
  watch(
    () => notificationsStore.isDrawerOpen,
    (isOpen) => {
      if (isOpen) {
        nextTick(() => {
          const overlay = document.querySelector('.notifications-dialog .v-overlay__content');
          if (overlay && notificationsStore.position === 'below-bell' && notificationsStore.bellButtonRect) {
            const rect = notificationsStore.bellButtonRect;
            (overlay as HTMLElement).style.top = `${rect.bottom + 8}px`;
            (overlay as HTMLElement).style.right = `${window.innerWidth - rect.right}px`;
            (overlay as HTMLElement).style.left = 'auto';
          }
        });
      }
    }
  );

  onUnmounted(() => {
    document.removeEventListener('keydown', handleEsc);
  });
});
</script>

<template>
  <v-layout class="d-flex flex-column">
    <TheNavbar v-if="showNavbar" />
    <!-- Pop-up notification container -->
    <AppNotificationsContainer v-if="notificationsStore.isDrawerOpen" />
    <v-main class="flex-grow-1">
      <div class="app-page">
        <NuxtPage />
      </div>
      <TheSnackbar />
    </v-main>
    <TheFooter v-if="showNavbar" />
  </v-layout>
</template>

<style lang="scss" scoped>
@use 'vuetify/settings' as *;
@use 'sass:map';
@use '#core/assets/scss/partials/colors' as *;

.v-layout {
  min-height: 100vh;
}

.v-main {
  padding-inline: 16px !important;
  padding-bottom: 0 !important;
  background-color: #f7f7f8;
  /* background: linear-gradient(135deg, $background-primary 0%, $background-secondary 100%) !important; */
  display: flex;
  flex-direction: column;
}

.app-page {
  flex: 1;
}
</style>

<style lang="scss">
:deep(.notifications-dialog) {
  .v-overlay__content {
    position: fixed !important;
    top: 80px !important;
    right: 16px !important;
    left: auto !important;
    transform: none !important;
    max-width: 588px !important;
    width: calc(100vw - 32px) !important;
    max-height: calc(100vh - 100px) !important;
    margin: 0 !important;
    z-index: 2500 !important;
  }

  @media (max-width: 768px) {
    .v-overlay__content {
      top: 72px !important;
      right: 8px !important;
      left: 8px !important;
      width: calc(100vw - 16px) !important;
      max-width: none !important;
      z-index: 2500 !important;
    }
  }
}
</style>
